import { Component, type ErrorInfo, type ReactNode, Suspense } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Button, Card, Spinner } from '@crm/ui';

export function RouteLoading() {
  const { t } = useTranslation();
  return <Card className="flex min-h-56 items-center justify-center p-8" role="status" data-testid="route-loading"><Spinner label={t('app.loadingModule')} /></Card>;
}

interface BoundaryProps { resetKey: string; children: ReactNode; title: string; detail: string; retry: string; reload: string }
interface BoundaryState { error: Error | null }

class RouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): BoundaryState { return { error }; }
  override componentDidCatch(error: Error, info: ErrorInfo) { console.error('Erreur d’affichage du module', error, info); }
  override componentDidUpdate(previous: BoundaryProps) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }
  override render() {
    if (!this.state.error) return this.props.children;
    return <Card className="mx-auto max-w-2xl border-red-200 p-6" role="alert" data-testid="route-error">
      <div className="flex gap-3"><span className="rounded-full bg-red-50 p-2 text-red-700"><AlertTriangle size={22} aria-hidden /></span><div><h1 className="font-heading text-lg font-semibold text-slate-900">{this.props.title}</h1><p className="mt-1 text-sm text-slate-600">{this.props.detail}</p></div></div>
      <div className="mt-5 flex flex-wrap gap-2"><Button onClick={() => this.setState({ error: null })}>{this.props.retry}</Button><Button variant="secondary" onClick={() => window.location.reload()}><RefreshCw size={16} />{this.props.reload}</Button></div>
    </Card>;
  }
}

/** Garde un retour visuel pendant le chargement et remplace toute page blanche par une reprise explicite. */
export function RouteOutletGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { t } = useTranslation();
  return <RouteErrorBoundary resetKey={location.key} title={t('app.moduleError.title')} detail={t('app.moduleError.detail')} retry={t('app.moduleError.retry')} reload={t('app.moduleError.reload')}>
    <Suspense key={location.pathname} fallback={<RouteLoading />}>{children}</Suspense>
  </RouteErrorBoundary>;
}

const RELOAD_KEY = 'crm-route-chunk-reload';

/** Après un déploiement, recharge une seule fois le nouveau manifeste si un ancien onglet demande un chunk disparu. */
export function recoverLazyImport<T>(factory: () => Promise<T>): Promise<T> {
  return factory().then((module) => {
    sessionStorage.removeItem(RELOAD_KEY);
    return module;
  }).catch((error: unknown) => {
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
      return new Promise<T>(() => undefined);
    }
    sessionStorage.removeItem(RELOAD_KEY);
    throw error;
  });
}
